import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TabBase } from '@core/base/tab.base';
import { PendingChanges } from '@core/guards/pending-changes.guard';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { DvDynamicFormUtils, DvFormUtils, FormOf } from '@devinco/ui/legacy';
import { SplitterWithGridHelperService } from '@features/vt-grid/services/splitter-with-grid-helper.service';
import { Entity, NEW_ENTITY_ROUTE_PARAM } from '@shared/constants/entity';
import { UserDto } from '@shared/interfaces/user.model';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { TranslateService } from '@ngx-translate/core';
import { RoleDto } from '@pages/settings-page/access-control-settings/roles/grid/interfaces';
import {
  AllowedDepartment,
  DeactivatedUserResp,
  Role,
  UserInformationTabFormGroup,
} from './interfaces';
import DynamicFormUtils from '@shared/utils/dynamic-form.utils';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { first, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { UserInformationTabService } from './user-information-tab.service';
import { DialogService } from '@core/services/dialog.service';
import { DvDynamicFormElement } from '@devinco/ui/legacy';
@Component({
  selector: 'app-user-information-tab',
  templateUrl: './user-information-tab.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UserInformationTabComponent
  extends TabBase
  implements PendingChanges, OnInit
{
  private tab = inject(UserInformationTabService);
  private readonly api = inject(RestApiService);
  private agGridEntityService = inject(SplitterWithGridHelperService);
  private readonly oidc = inject(OidcSecurityService);
  private userRolesAndPermissionsService = inject(
    UserRolesAndPermissionsService
  );
  private dialog = inject(DialogService);
  private translate = inject(TranslateService);

  formGroup: FormGroup<FormOf<UserInformationTabFormGroup>>;
  personalInfoElements: DvDynamicFormElement[];
  membershipElements: DvDynamicFormElement[];
  allowedDepartmentsElements: DvDynamicFormElement[];
  allElements: DvDynamicFormElement[];

  createdTime = signal<Date | undefined>(undefined);
  saveInProgress = signal<boolean>(false);

  private destroyRef = inject(DestroyRef);

  constructor() {
    super(inject(ActivatedRoute).parent!);
  }

  ngOnInit(): void {
    this.setupVariables();
    this.setupEvents();
  }

  setupVariables(): void {
    this.personalInfoElements = this.tab.getPersonalInfoElements();
    this.membershipElements = this.tab.getMembershipElements(this.roleMapFn);
    this.allowedDepartmentsElements = this.tab.getAllowedDepartmentsElements();
    this.allElements = [
      ...this.personalInfoElements,
      ...this.membershipElements,
      ...this.allowedDepartmentsElements,
    ];
    this.formGroup = DvFormUtils.createFormGroup(this.allElements, true);
  }

  hasChanges = (): boolean => this.formGroup.dirty && this.formGroup.valid;
  pendingChangesSave = (): Observable<boolean> => this.performSave();

  onSave(): void {
    this.performSave().subscribe();
  }

  private roleMapFn = (roles: RoleDto[]): (RoleDto & { disabled: boolean })[] =>
    roles.map(role => ({ ...role, disabled: !role.Enabled }));

  private performSave(): Observable<boolean> {
    const dirtyValues = DvFormUtils.getDirtyValues(this.formGroup);

    let data = dirtyValues;
    if ('AllowDepartments' in dirtyValues) {
      data = {
        ...data,
        AllowDepartments: this.formGroup.controls.AllowDepartments?.value?.map(
          departmentId => ({ DepartmentID: departmentId })
        ),
      };
    }

    this.saveInProgress.set(true);
    if (this.entityId$.value === NEW_ENTITY_ROUTE_PARAM) {
      const userName = this.formGroup.controls.Username.value;
      return this.api
        .getCustom<DeactivatedUserResp>(
          `${Entity.User}/DeactivatedUser/${userName}`
        )
        .pipe(
          switchMap(resp => {
            if (resp.UserID) {
              return this.dialog
                .open<undefined, unknown>({
                  size: 'md',
                  title: 'User.ReactivateUserTitle',
                  contentText: this.translate.instant(
                    'User.ReactivateUserText',
                    {
                      userName: userName,
                    }
                  ),
                  confirmText: 'General.Save',
                  cancelText: 'General.Cancel',
                })
                .closed.pipe(
                  switchMap(({ source }) => {
                    if (source === 'confirm') {
                      return this.upsertUser(
                        String(resp.UserID),
                        data as UserInformationTabFormGroup,
                        true
                      );
                    }
                    this.saveInProgress.set(false);
                    return of(true);
                  })
                );
            } else {
              return this.upsertUser(
                String(resp.UserID),
                data as UserInformationTabFormGroup
              );
            }
          })
        );
    } else {
      return this.upsertUser(
        this.entityId$.value,
        data as UserInformationTabFormGroup
      );
    }
  }

  private upsertUser(
    entityId: string,
    data: UserInformationTabFormGroup,
    isReactivation = false
  ): Observable<boolean> {
    return this.api.upsert(Entity.User, entityId, data).pipe(
      tap((userId: number | undefined) => {
        if (userId || isReactivation) {
          this.agGridEntityService.addNewlyCreatedItemToGrid<
            UserDto,
            Omit<UserDto, 'Roles'> & { Roles: string }
          >(Entity.User, userId || Number(entityId), user => ({
            ...user,
            Roles: user.Roles?.map(role => role.Name).join(', ') || '',
          }));
        } else {
          this.agGridEntityService.refreshRowData(entityId);
        }
        this.formGroup.markAsPristine();
      }),
      tap(() => this.saveInProgress.set(false)),
      map(() => true)
    );
  }

  private setupEvents(): void {
    this.entityId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(id => {
      if (!id) return;
      if (id === NEW_ENTITY_ROUTE_PARAM) {
        this.formGroup.reset();
        this.setRolesValue([]);
        this.toggleUsername(true);
        this.createdTime.set(undefined);
        return;
      }

      const keys = DvDynamicFormUtils.getKeysForRequest(
        this.allElements
      ).filter(key => {
        return key !== 'TagDescription' && key !== 'Name';
      });
      this.api.getInfo<UserDto>('User', id, keys).subscribe((res: UserDto) => {
        this.createdTime.set(res.CreatedTime);
        const data = this.tab.prepareDataForForm(res);
        DvDynamicFormUtils.setFirstData(
          this.allElements.filter(el => el.options.key !== 'AllowDepartments'),
          this.formGroup,
          data
        );
        this.toggleUsername(false);
        this.setRolesValue(res.Roles || []);
        this.setAllowDepartmentsValue(res.AllowDepartments || []);
        this.toggleValidationForPhoneNumber();
        this.formGroup.markAsPristine();

        this.oidc.userData$.pipe(first()).subscribe(userData => {
          if (
            res.Username.toLowerCase() === userData.userData.email.toLowerCase()
          ) {
            this.formGroup.controls.RoleTypeID?.disable();
          } else {
            this.formGroup.controls.RoleTypeID?.enable();
          }
        });

        this.handleAccessRestrictions();
      });
    });
  }

  private setAllowDepartmentsValue(
    allowedDepartments: AllowedDepartment[]
  ): void {
    const departments = allowedDepartments.map(
      department => department.DepartmentID
    );

    DynamicFormUtils.setDropdownElementItems<AllowedDepartment>(
      this.allElements,
      'AllowDepartments',
      allowedDepartments
    );

    this.formGroup.controls.AllowDepartments?.setValue(departments);
  }

  private setRolesValue(roles: Role[]): void {
    const selectedRoles = roles.map(role => role.RoleID);

    DynamicFormUtils.setTranslatedDropdownElementItems<RoleDto>(
      this.membershipElements,
      'Roles',
      roles,
      this.translate,
      'TranslationKey',
      undefined,
      'Name'
    );

    this.formGroup.controls.Roles?.setValue(selectedRoles, {
      emitEvent: false,
    });
  }

  private handleAccessRestrictions(): void {
    this.userRolesAndPermissionsService
      .hasPermissions([RolesAndPermissions.UserEdit])
      .pipe(take(1))
      .subscribe(hasPermissions => {
        if (!hasPermissions) {
          this.formGroup.disable();
        }
      });
  }

  private toggleValidationForPhoneNumber(): void {
    if (!this.formGroup.controls.Mobile.valid) {
      this.formGroup.controls.Mobile.markAsDirty();
    }
  }

  private toggleUsername(enable: boolean): void {
    const usernameControl = this.formGroup.controls.Username;
    enable ? usernameControl.enable() : usernameControl.disable();
  }
}
